import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { getExpenseForecastComparision } from "../../utils/ActionCreator";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  const forecast = payload.find((p) => p.dataKey === "forecast")?.value;
  const actual = payload.find((p) => p.dataKey === "actual")?.value;
  const diff = actual - forecast;
  const isOver = diff > 0;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        padding: "14px 16px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
        minWidth: "180px",
      }}
    >
      <div
        style={{
          fontSize: "11px",
          fontWeight: 700,
          color: "#94a3b8",
          marginBottom: "10px",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        {label}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "6px",
          gap: "24px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: "10px",
              height: "10px",
              borderRadius: "3px",
              backgroundColor: "#101313",
            }}
          />
          <span style={{ fontSize: "12px", color: "#64748b" }}>Forecast</span>
        </div>
        <span style={{ fontSize: "13px", fontWeight: 700, color: "#0f172a" }}>
          ₹{forecast?.toLocaleString()}
        </span>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "24px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: "10px",
              height: "10px",
              borderRadius: "3px",
              backgroundColor: "#0f766e",
            }}
          />
          <span style={{ fontSize: "12px", color: "#64748b" }}>Actual</span>
        </div>
        <span style={{ fontSize: "13px", fontWeight: 700, color: "#0f172a" }}>
          ₹{actual?.toLocaleString()}
        </span>
      </div>

      <div
        style={{
          borderTop: "1px solid #f1f5f9",
          margin: "10px 0 8px",
        }}
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ fontSize: "11px", color: "#94a3b8" }}>Variance</span>
        <span
          style={{
            fontSize: "12px",
            fontWeight: 700,
            color: isOver ? "#ef4444" : "#10b981",
            background: isOver ? "#fef2f2" : "#f0fdf4",
            padding: "2px 8px",
            borderRadius: "20px",
          }}
        >
          {isOver ? "+" : ""}
          {diff?.toLocaleString()}
        </span>
      </div>
    </div>
  );
};

export default function ExpenseForecastChart({ dateFilter, setDateFilter, onReset }) {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    if (!dateFilter?.from || !dateFilter?.to) return;
    setLoading(true);
    getExpenseForecastComparision(
      dateFilter?.from,
      dateFilter?.to,
      (data) => {
        setLoading(false);
        const formatted = data.map((item) => ({
          month: item.month,
          forecast: item.foreCastAmount || 1,
          actual: item.amount || 1,
        }));
        setChartData(formatted);
      },
      (error) => {
        console.error(error);
        setLoading(false);
      },
    );
  }, [dateFilter]);

  return (
    <>
      <style>{`
        @keyframes shimmer { from{background-position:200% 0} to{background-position:-200% 0} }
        @keyframes fadeSlideIn { from{opacity:0;transform:translateX(-12px)} to{opacity:1;transform:translateX(0)} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes pulseOpacity { 0%,100%{opacity:1} 50%{opacity:0.4} }

        .efc-container {
          background: #fff;
          padding: 20px;
          border-radius: 12px;
          border: 1px solid #E5E7EB;
          height: 100%;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-sizing: border-box;
        }

        .efc-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          margin-bottom: 15px;
          flex-wrap: nowrap;
          width: 100%;
          min-width: 0;
          box-sizing: border-box;
        }

        .efc-header-title {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
          min-width: 0;
        }

        .efc-header-indicator {
          width: 8px;
          height: 8px;
          min-width: 8px;
          border-radius: 50%;
          background: #036B65;
          box-shadow: 0 0 0 3px rgba(3,107,101,0.2);
        }

        .efc-title {
          margin: 0;
          font-size: 17px;
          font-weight: 700;
          color: #0d2b29;
          letter-spacing: -0.3px;
          white-space: nowrap;
        }

        .efc-date-filters {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-shrink: 1;
          min-width: 0;
          overflow: hidden;
        }

        .efc-date-filters input[type="date"] {
          border: 1px solid rgba(3,107,101,0.2);
          border-radius: 8px;
          padding: 5px 8px;
          font-size: 12px;
          color: #036B65;
          outline: none;
          background: rgba(3,107,101,0.04);
          box-sizing: border-box;
          width: 130px;
          min-width: 0;
          flex-shrink: 1;
        }

        .efc-reset-btn {
          display: flex;
          align-items: center;
          gap: 5px;
          border: 1px solid rgba(3,107,101,0.2);
          border-radius: 8px;
          padding: 5px 10px;
          font-size: 12px;
          color: #036B65;
          background: rgba(3,107,101,0.04);
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .efc-reset-btn:hover {
          background: rgba(3,107,101,0.12);
          border-color: rgba(3,107,101,0.4);
        }

        .efc-reset-label { display: inline; }

        @media (max-width: 1024px) {
          .efc-container { padding: 16px; }
          .efc-title { font-size: 15px; }
          .efc-header-indicator { width: 7px; height: 7px; min-width: 7px; }
          .efc-date-filters input[type="date"] { width: 118px; font-size: 11px; padding: 4px 6px; }
          .efc-reset-btn { font-size: 11px; padding: 4px 8px; }
        }

        @media (max-width: 768px) {
          .efc-container { padding: 12px; border-radius: 8px; }
          .efc-title { font-size: 13px; }
          .efc-header-indicator { width: 6px; height: 6px; min-width: 6px; }
          .efc-date-filters { gap: 4px; }
          .efc-date-filters input[type="date"] { width: 100px; font-size: 10px; padding: 3px 5px; }
          .efc-reset-btn { font-size: 10px; padding: 3px 7px; gap: 3px; }
        }

        @media (max-width: 600px) {
          .efc-container { padding: 10px; border-radius: 6px; }
          .efc-title { font-size: 12px; }
          .efc-header { gap: 5px; }
          .efc-header-indicator { width: 5px; height: 5px; min-width: 5px; }
          .efc-date-filters { gap: 3px; }
          .efc-date-filters input[type="date"] { width: 88px; font-size: 9px; padding: 3px 3px; }
          .efc-reset-btn { padding: 3px 6px; font-size: 9px; gap: 3px; }
          .efc-reset-label { display: none; }
        }
      `}</style>

      <div className="efc-container">
        <div className="efc-header">
          <div className="efc-header-title">
            <div
              className="efc-header-indicator"
              style={{
                animation: loading ? "pulseOpacity 1.5s infinite" : "none",
              }}
            />
            <h2 className="efc-title">Expense vs Forecast</h2>
          </div>

          <div className="efc-date-filters">
            <input
              type="date"
              value={dateFilter?.from}
              onChange={(e) =>
                setDateFilter({ ...dateFilter, from: e.target.value })
              }
            />
            <input
              type="date"
              value={dateFilter?.to}
              onChange={(e) =>
                setDateFilter({ ...dateFilter, to: e.target.value })
              }
            />
            <button
              className="efc-reset-btn"
              onClick={onReset}
              title="Reset to default date range"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#036B65"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
              </svg>
              <span className="efc-reset-label">Reset</span>
            </button>
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} barCategoryGap={30}>
              <CartesianGrid stroke="#f1f5f9" vertical={false} />

              <defs>
                <linearGradient id="actualGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#37837b" />
                  <stop offset="100%" stopColor="#c8f8f4" />
                </linearGradient>
                <linearGradient id="forcastGredient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#b9b9ce" />
                  <stop offset="100%" stopColor="#101313" />
                </linearGradient>
              </defs>

              <XAxis dataKey="month" axisLine={false} tickLine={false} />
              <YAxis
                axisLine={false}
                tickLine={false}
                scale="log"
                domain={[1, "auto"]}
                allowDataOverflow
                tickFormatter={(v) => {
                  if (v >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
                  if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
                  if (v >= 1000) return `₹${(v / 1000).toFixed(1)}k`;
                  return `₹${v}`;
                }}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: "#f8fafc", radius: 6 }}
              />
              <Legend
                content={() => (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "20px",
                      paddingTop: "12px",
                    }}
                  >
                    {[
                      { color: "#101313", label: "Forecast" },
                      { color: "#0f766e", label: "Actual" },
                    ].map((item) => (
                      <div
                        key={item.label}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "7px",
                          background: "#f8fafc",
                          padding: "5px 12px",
                          borderRadius: "20px",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <div
                          style={{
                            width: "10px",
                            height: "10px",
                            borderRadius: "3px",
                            backgroundColor: item.color,
                          }}
                        />
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#475569",
                            fontWeight: 600,
                          }}
                        >
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              />
              <Bar
                dataKey="forecast"
                name="Forecast"
                fill="url(#forcastGredient)"
                radius={[6, 6, 0, 0]}
                barSize={28}
              />
              <Bar
                dataKey="actual"
                name="Actual"
                fill="url(#actualGradient)"
                radius={[6, 6, 0, 0]}
                barSize={28}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
}